import { Link } from 'react-router-dom';
import { 
  Monitor, Shield, Cloud, Network, PenTool, 
  Cable, Wrench, Lock 
} from 'lucide-react';

interface ServiceNavigationProps {
  currentService?: string;
  className?: string;
}

export function ServiceNavigation({ currentService, className = "" }: ServiceNavigationProps) {
  const services = [
    {
      name: 'IT Support',
      slug: 'it-support',
      icon: Monitor,
      description: '24/7 monitoring and technical support'
    },
    {
      name: 'Cybersecurity',
      slug: 'cybersecurity',
      icon: Shield,
      description: 'Advanced threat protection and security'
    },
    {
      name: 'Microsoft 365',
      slug: 'microsoft-365',
      icon: Cloud,
      description: 'Cloud productivity and collaboration'
    },
    {
      name: 'Network Solutions',
      slug: 'network',
      icon: Network,
      description: 'Professional network infrastructure'
    },
    {
      name: 'IT Projects',
      slug: 'projects',
      icon: PenTool,
      description: 'Expert project management'
    },
    {
      name: 'Security & Access',
      slug: 'security-cameras',
      icon: Lock,
      description: 'Surveillance and access control'
    },
    {
      name: 'Network Cabling',
      slug: 'cabling',
      icon: Cable,
      description: 'Structured cabling solutions'
    },
    {
      name: 'Computer Repair',
      slug: 'computer-repair',
      icon: Wrench,
      description: 'On-site repair and maintenance'
    }
  ];

  return (
    <section className={`py-12 bg-white dark:bg-gray-800 ${className}`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-900 dark:text-white">
            Our Complete IT Services
          </h2>
          <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
            Comprehensive technology solutions for businesses throughout the Treasure Valley.
          </p>
        </div>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {services.map((service) => {
            const Icon = service.icon;
            const isActive = currentService === service.slug;
            
            return (
              <Link
                key={service.slug}
                to={`/services/${service.slug}`}
                className={`group block p-6 rounded-xl transition-all duration-300 hover:shadow-lg hover:-translate-y-1 ${
                  isActive
                    ? 'bg-primary-50 dark:bg-primary-900/20 border-2 border-primary-200 dark:border-primary-700'
                    : 'bg-gray-50 dark:bg-gray-900 hover:bg-white dark:hover:bg-gray-800 border-2 border-transparent'
                }`}
              >
                <div className="flex items-start space-x-4">
                  <div className={`p-3 rounded-lg ${
                    isActive 
                      ? 'bg-primary-100 dark:bg-primary-800' 
                      : 'bg-primary-50 dark:bg-primary-900/20 group-hover:bg-primary-100 dark:group-hover:bg-primary-800'
                  } transition-colors`}>
                    <Icon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <div>
                    <h3 className={`text-lg font-semibold mb-2 transition-colors ${
                      isActive
                        ? 'text-primary-900 dark:text-primary-100'
                        : 'text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400'
                    }`}>
                      {service.name}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-300 text-sm">
                      {service.description}
                    </p>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}